/**
 * JRS Logo Service
 * Version: 0.2.0 (Persistence layer added — 2026-05-11)
 *
 * Responsible for everything logo-related:
 *   - Uploading files to jrsindustrial-files.com (v0.1 — unchanged)
 *   - Client-side file validation (v0.1 — unchanged)
 *   - Filename sanitisation + canonical "logo_{ts}_{name}.{ext}" formatting
 *     (v0.1 — unchanged)
 *   - Reading the customer's saved-logo library from Liquid OR localStorage
 *     (v0.2 — new)
 *   - Writing new logos to the cloud (Cloudflare Worker via App Proxy) OR
 *     localStorage as the offline / guest fallback (v0.2 — new)
 *   - Touching existing logos to refresh last_used_at sort order (v0.2 — new)
 *   - One-time migration of guest localStorage logos into the cloud on first
 *     authenticated popup open (v0.2 — new)
 *
 * Dependencies: none required. Will use JRS.generateUUID if present, otherwise
 * crypto.randomUUID or a Math.random fallback.
 *
 * ============================================================================
 * Revision notes (v0.2.0 — Doc 4 v0.2 persistence layer)
 * ============================================================================
 *
 * Everything from v0.1 (uploadFile, validateFile, sanitizeFilename,
 * buildUploadFilename) is preserved unchanged. The new persistence methods
 * are additive; existing callers continue to work.
 *
 * New public methods on JRS.LogoService:
 *   - readSavedLogos()              → { logos, isLoggedIn, hasCloudLogos }
 *   - saveLogo(logo)                → Promise<{ ok: boolean, logos: SavedLogo[] }>
 *   - touchLogo(id)                 → Promise<boolean>  (silently no-ops on failure)
 *   - deleteLogo(id)                → Promise<{ ok: boolean, logos: SavedLogo[] }>
 *   - runMigration()                → Promise<{ migrated: boolean, count: number }>
 *   - isLoggedIn()                  → boolean
 *
 * Internal helpers (not exported):
 *   - _readLocalLogos()             — parses localStorage.jrs.logos.v1
 *   - _writeLocalLogos(logos)       — writes with last_used_at back-fill
 *   - _touchLocalLogo(id)           — updates last_used_at on one entry
 *   - _readCloudLogosBlob()         — parses the Liquid <script> blob
 *   - _workerFetch(method, path, body) — wraps fetch with App Proxy URL +
 *                                        timeout + uniform error handling
 *
 * Worker addressing:
 *   The popup hits the Shopify App Proxy at /apps/jrs-logos/* on the storefront
 *   origin (Doc 4 §10.3 / §11.2). Shopify forwards to the Cloudflare Worker
 *   with App Proxy signature query params. The client just sends a normal
 *   same-origin fetch; no auth handling needed here.
 *
 *   If the App Proxy subpath changes in Shopify (Settings → Apps → JRS
 *   Customizer Logo Service → App proxy), update WORKER_BASE_PATH below.
 *
 * Session-scoped touch debounce:
 *   Each popup session may select a given logo many times across positions.
 *   Per Doc 4 §4.2 the touch fires at most once per (logo id, popup session)
 *   to avoid flooding the Worker with no-op writes. The debounce set is
 *   reset by resetTouchDebounce() which the popup calls on each new open.
 *
 * No breaking changes. uploadFile / validateFile / sanitizeFilename /
 * buildUploadFilename signatures are byte-identical to v0.1.
 * ============================================================================
 */

window.JRS = window.JRS || {};

(function(JRS) {
  'use strict';

  // ===========================================================================
  // Constants (existing v0.1 + new v0.2)
  // ===========================================================================

  /** Module version, surfaced for debugging. */
  var VERSION = '0.2.0';

  // ----- File upload (v0.1) -----

  var UPLOAD_ENDPOINT = 'https://jrsindustrial-files.com';
  var MAX_FILE_SIZE_BYTES = 10 * 1024 * 1024;
  var generateUUID = JRS.generateUUID;

  var ACCEPTED_MIME_TYPES = {
    'image/jpeg': true,
    'image/png': true,
    'image/gif': true,
    'image/svg+xml': true,
    'image/webp': true,
    'image/bmp': true,
    'image/tiff': true,
    // PDF accepted (2026-08-01, control-theme parity): the design team works
    // from PDF artwork directly — control has accepted it since 2026-07-24
    // and the upload server takes it. Supersedes the old Doc 2 §12.4
    // explicit-PDF-rejection rule for this theme.
    'application/pdf': true
  };

  var ACCEPTED_EXTENSIONS = {
    jpg: true,
    jpeg: true,
    png: true,
    gif: true,
    svg: true,
    webp: true,
    bmp: true,
    tif: true,
    tiff: true,
    pdf: true
  };

  var REJECTED_MIME_TYPES = {
    'application/msword': true,
    'application/vnd': true,
    'application/zip': true,
    'application/x-rar': true
  };

  var REJECTED_EXTENSIONS = {
    doc: true,
    docx: true,
    xls: true,
    xlsx: true,
    zip: true,
    rar: true
  };

  // ---- Non-image preview handling (2026-08-01, ported from control) --------
  // A PDF can't render in an <img>; control substitutes an inline document
  // placeholder wherever a logo thumbnail is shown (its NON_IMAGE_THUMB /
  // isDisplayableImageLogo pair, 2026-07-24). Shared here so every v2 preview
  // surface (dropzone preview, saved-logo tiles, bundle summary, cart page)
  // renders the same badge instead of a broken image.

  var IMAGE_PREVIEW_EXTS = {
    png: true, jpg: true, jpeg: true, gif: true, svg: true,
    webp: true, bmp: true, tif: true, tiff: true
  };

  /** Lowercased extension of a filename/URL (query/hash stripped), '' if
      none. Only the LAST path segment is examined so a dot in the domain
      (x.com/blob) never reads as an extension. */
  function _previewExt(nameOrUrl) {
    if (!nameOrUrl) return '';
    var clean = String(nameOrUrl).split('?')[0].split('#')[0].toLowerCase();
    var seg = clean.substring(clean.lastIndexOf('/') + 1);
    var dot = seg.lastIndexOf('.');
    return dot === -1 ? '' : seg.substring(dot + 1);
  }

  /**
   * True when the file behind this name/URL can render in an <img>. No
   * extension hint → assume image and let the <img> try (control behaviour).
   */
  function isDisplayableImage(nameOrUrl) {
    var ext = _previewExt(nameOrUrl);
    if (!ext) return true;
    return IMAGE_PREVIEW_EXTS[ext] === true;
  }

  /**
   * Inline SVG document placeholder (data URI) labelled with the file's
   * extension (PDF etc.; 'FILE' fallback) — the "pdf icon" for previews.
   */
  function nonImageThumb(nameOrUrl) {
    var ext = _previewExt(nameOrUrl);
    var label = (ext && ext.length <= 4) ? ext.toUpperCase() : 'FILE';
    return 'data:image/svg+xml;utf8,' + encodeURIComponent(
      '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 48 48">' +
      '<rect width="48" height="48" rx="6" fill="#f1f1f1"/>' +
      '<path d="M14 8h13l9 9v23a2 2 0 0 1-2 2H14a2 2 0 0 1-2-2V10a2 2 0 0 1 2-2z" fill="#fff" stroke="#c4c4c4" stroke-width="1.5"/>' +
      '<path d="M27 8v9h9" fill="none" stroke="#c4c4c4" stroke-width="1.5"/>' +
      '<text x="24" y="32" font-family="Arial, sans-serif" font-size="9" font-weight="bold" fill="#8a8a8a" text-anchor="middle">' + label + '</text>' +
      '</svg>'
    );
  }

  /**
   * The src a logo preview <img> should use: the real URL for displayable
   * images, the document placeholder for PDF (and any other non-image).
   */
  function previewSrcFor(url, nameHint) {
    return isDisplayableImage(nameHint || url) ? url : nonImageThumb(nameHint || url);
  }

  // ----- Persistence layer (v0.2) -----

  /**
   * App Proxy subpath. Configured in Shopify Admin → Settings → Apps →
   * JRS Customizer Logo Service → App proxy. If the subpath is changed
   * there, change this constant.
   * Per Doc 4 §10.3.
   */
  var WORKER_BASE_PATH = '/apps/jrs-logos';

  /** localStorage key for the customer's saved-logo library (Doc 4 §6.1). */
  var LOCAL_STORAGE_KEY = 'jrs.logos.v1';

  /**
   * localStorage key for the one-time migration sentinel (Doc 4 §8.4).
   * Once set, the migration does not run again on this device.
   */
  var MIGRATION_SENTINEL_KEY = 'jrs.migration.v1.done';

  /** DOM selector for the Liquid-rendered cloud-logos blob (Doc 4 §3.1). */
  var CLOUD_BLOB_SELECTOR = 'script[data-jrs-customer-logos]';

  /**
   * Default request timeout for Worker calls. Cloud writes are fire-and-forget
   * from the popup's perspective; this is the cap before we give up waiting
   * and fall back to localStorage.
   */
  var WORKER_TIMEOUT_MS = 8000;

  /**
   * Cap on logos per customer (Doc 4 §2.3). Enforced server-side too; this
   * is a defensive client-side cap so localStorage doesn't grow unbounded
   * for guests who never log in.
   */
  var MAX_LOGOS = 50;

  // ===========================================================================
  // Internal state (v0.2)
  // ===========================================================================

  /**
   * Set of logo ids already touched in the current popup session. Per Doc 4
   * §4.2 the touch fires at most once per id per session. Reset by
   * resetTouchDebounce() which the popup shell calls on each OPEN dispatch.
   */
  var _touchedThisSession = {};

  // ===========================================================================
  // File upload (v0.1 — unchanged)
  // ===========================================================================

  /**
   * Upload a logo file to the JRS file server.
   * Returns a structured result with UUID-based logo ID.
   *
   * @param {File} file - Browser File object from input[type=file] or drag-and-drop
   * @param {function} [onProgress] - Optional callback: function(percentComplete) where 0-100
   * @returns {Promise<import('./jrs-v2-types.js').LogoUploadResult>}
   *   Resolves: { logoId: string, url: string, filename: string }
   *   Rejects: Error with descriptive message
   */
  // Doc 10 P3 / W1 — SHA-256 of the file bytes as "sha256:<hex>". Best-effort:
  // returns '' when crypto.subtle or File.arrayBuffer is unavailable (old engines,
  // non-secure context, jsdom). Never rejects — logo integrity is diagnostic only.
  function _computeFileSha(file) {
    try {
      if (!file || typeof file.arrayBuffer !== 'function' ||
          typeof crypto === 'undefined' || !crypto.subtle ||
          typeof crypto.subtle.digest !== 'function') {
        return Promise.resolve('');
      }
      return file.arrayBuffer()
        .then(function (buf) { return crypto.subtle.digest('SHA-256', buf); })
        .then(function (hashBuf) {
          var bytes = new Uint8Array(hashBuf);
          var hex = '';
          for (var i = 0; i < bytes.length; i++) { hex += ('0' + bytes[i].toString(16)).slice(-2); }
          return 'sha256:' + hex;
        })
        .catch(function () { return ''; });
    } catch (e) {
      return Promise.resolve('');
    }
  }

  function uploadFile(file, onProgress) {
    var validation = validateFile(file);
    if (!validation.valid) {
      return Promise.reject(new Error(validation.error || 'File validation failed'));
    }

    var logoId = mintLogoId();
    var uploadName = buildUploadFilename(file.name || 'logo.png');
    var formData = new FormData();
    formData.append('file', file, uploadName);

    return new Promise(function(resolve, reject) {
      var xhr = new XMLHttpRequest();
      xhr.open('POST', UPLOAD_ENDPOINT, true);
      xhr.timeout = 60000;

      if (typeof onProgress === 'function' && xhr.upload) {
        xhr.upload.addEventListener('progress', function(e) {
          if (!e.lengthComputable) return;
          var percent = Math.round((e.loaded / e.total) * 100);
          onProgress(percent);
        });
      }

      xhr.onload = function() {
        if (xhr.status >= 200 && xhr.status < 300) {
          var fileUrl = _extractFileUrl(xhr.responseText || '');
          if (!fileUrl || fileUrl.indexOf('https://') !== 0) {
            reject(new Error('Upload failed — server did not return a valid HTTPS URL.'));
            return;
          }

          // Doc 10 P3 / W1 — a client-side SHA-256 of the exact bytes we uploaded.
          // Carried into the parent _jrs_provenance.logo_sha so fulfilment can
          // re-hash the file at the URL and PROVE it matches what the customer
          // sent ("uploaded X, got Y"). Best-effort — resolves '' where the
          // engine lacks crypto.subtle / File.arrayBuffer; never blocks the add.
          _computeFileSha(file).then(function (sha) {
            resolve({
              logoId: logoId,
              url: fileUrl,
              filename: file.name,
              sha: sha
            });
          });
          return;
        }

        reject(new Error('Upload failed with status ' + xhr.status + '. Please try again.'));
      };

      xhr.onerror = function() {
        reject(new Error('Upload failed — network error. Please check your connection and try again.'));
      };

      xhr.ontimeout = function() {
        reject(new Error('Upload timed out. Please try again.'));
      };

      xhr.send(formData);
    });
  }

  /**
   * Validate a file before upload. Checks type, extension, and size.
   *
   * Per Doc 2 §12.4 (client-side upload validation contract) and
   * Doc 7 §7.5 (canonical error copy keys). Returns BOTH a translation
   * key and a byte-matching English fallback string so callers can
   * render via the theme's translation function ({{ errorKey | t }}
   * in Liquid; the equivalent JS lookup at the step-controller layer)
   * and use `error` only when translation is unavailable.
   *
   * @param {File} file - Browser File object
   * @returns {{ valid: boolean, errorKey: string|null, error: string|null }}
   *           errorKey is a Doc 7 §7.5 translation key (e.g. 'customizer.errors.file_too_large');
   *           error is the byte-matching English fallback. Callers should
   *           render via the theme's translation function and use `error`
   *           only if translation is unavailable.
   */
  function validateFile(file) {
    if (!file) {
      return {
        valid: false,
        errorKey: 'customizer.errors.file_missing',
        error: 'No file selected.'
      };
    }

    var fileName = typeof file.name === 'string' ? file.name : '';
    var fileType = typeof file.type === 'string' ? file.type.toLowerCase() : '';
    var extension = _getExtension(fileName);

    // PDF is ACCEPTED as of 2026-08-01 (control-theme parity; control has
    // taken PDF artwork since 2026-07-24). The old explicit-PDF-rejection
    // block that lived here is intentionally removed.

    // Explicitly reject known non-image document/archive types.
    if (_isRejectedMime(fileType) || REJECTED_EXTENSIONS[extension]) {
      return {
        valid: false,
        errorKey: 'customizer.errors.file_type_not_accepted',
        error: 'File type not accepted. Please upload JPG, PNG, GIF, SVG, WebP, or PDF.'
      };
    }

    var mimeAccepted = ACCEPTED_MIME_TYPES[fileType] === true;
    var extensionAccepted = ACCEPTED_EXTENSIONS[extension] === true;

    // Some browsers provide empty MIME type; extension fallback is required.
    if (!mimeAccepted && !extensionAccepted) {
      return {
        valid: false,
        errorKey: 'customizer.errors.file_type_not_accepted',
        error: 'File type not accepted. Please upload JPG, PNG, GIF, SVG, WebP, or PDF.'
      };
    }

    if (typeof file.size === 'number' && file.size > MAX_FILE_SIZE_BYTES) {
      return {
        valid: false,
        errorKey: 'customizer.errors.file_too_large',
        error: 'File is too large. Maximum size is 10 MB.'
      };
    }

    return { valid: true, errorKey: null, error: null };
  }

  /**
   * Sanitize a filename for safe upload.
   * Removes special characters, preserves extension.
   *
   * @param {string} filename - Original filename
   * @returns {string} Sanitized filename
   */
  function sanitizeFilename(filename) {
    var parts = _splitFilename(filename);
    var base = parts.name;
    var extension = parts.extension;

    base = base.replace(/[^A-Za-z0-9_-]+/g, '_');
    base = base.replace(/_+/g, '_');
    base = base.replace(/^_+|_+$/g, '');

    if (!base) {
      base = 'logo';
    }

    if (base.length > 50) {
      base = base.slice(0, 50);
    }

    if (!extension) {
      return base;
    }

    return base + '.' + extension;
  }

  /**
   * Build the upload filename with timestamp prefix.
   * Format: logo_{timestamp}_{sanitized_name}.{ext}
   *
   * @param {string} originalFilename
   * @returns {string}
   */
  function buildUploadFilename(originalFilename) {
    var sanitized = sanitizeFilename(originalFilename);
    var parts = _splitFilename(sanitized);
    var ts = Date.now();

    if (!parts.extension) {
      return 'logo_' + ts + '_' + parts.name;
    }

    return 'logo_' + ts + '_' + parts.name + '.' + parts.extension;
  }

  // ===========================================================================
  // v0.2 — Persistence layer
  // ===========================================================================

  /**
   * Determine whether the customer is logged in by checking for the
   * Liquid-rendered <script> tag (Doc 4 §3.1).
   *
   * @returns {boolean}
   */
  function isLoggedIn() {
    return !!document.querySelector(CLOUD_BLOB_SELECTOR);
  }

  /**
   * Read the customer's full saved-logo library, merging cloud + localStorage
   * sources per Doc 4 §3.2. Returns a single sorted array regardless of
   * source.
   *
   * Merge rule (Doc 4 §3.3): dedupe by id; when both sources have the same
   * id, keep the entry with the most recent last_used_at. Sort the final
   * array by last_used_at descending so the gallery shows most-recently-used
   * first.
   *
   * Back-fills last_used_at for legacy entries using uploaded_at as the
   * fallback. This is in-memory only; the metafield isn't rewritten until
   * the entry is next saved or touched (per Doc 4 §2.3 constraint).
   *
   * Safe to call before init: gracefully handles missing DOM elements,
   * missing localStorage, malformed JSON.
   *
   * @returns {{ logos: Array, isLoggedIn: boolean, hasCloudLogos: boolean }}
   */
  function readSavedLogos() {
    var loggedIn = isLoggedIn();
    var cloudLogos = loggedIn ? _readCloudLogosBlob() : [];
    var localLogos = _readLocalLogos();

    // Back-fill last_used_at for any legacy entry that lacks it. Uses
    // uploaded_at as the fallback. Sort-only — not written back to
    // either store until the entry is next saved or touched.
    var combined = cloudLogos.concat(localLogos);
    var backFilled = [];
    for (var i = 0; i < combined.length; i++) {
      var logo = combined[i];
      if (!logo || typeof logo.id !== 'string') continue;
      backFilled.push(_backFillLastUsedAt(logo));
    }

    // Merge: dedupe by id, keep entry with most recent last_used_at
    var byId = {};
    for (var j = 0; j < backFilled.length; j++) {
      var entry = backFilled[j];
      var existing = byId[entry.id];
      if (!existing || (entry.last_used_at || 0) > (existing.last_used_at || 0)) {
        byId[entry.id] = entry;
      }
    }

    // Convert back to array, sort by last_used_at desc
    var merged = [];
    for (var k in byId) {
      if (Object.prototype.hasOwnProperty.call(byId, k)) merged.push(byId[k]);
    }
    merged.sort(function(a, b) {
      return (b.last_used_at || 0) - (a.last_used_at || 0);
    });

    return {
      logos: merged,
      isLoggedIn: loggedIn,
      hasCloudLogos: cloudLogos.length > 0
    };
  }

  /**
   * Save a logo to the customer's library.
   *
   * For logged-in customers: fire-and-forget POST to the Worker with the
   * full SavedLogo shape. The Worker decides Add vs Update by checking
   * whether the id already exists (per Doc 4 §5.3).
   *
   * For guests: write directly to localStorage.
   *
   * In both cases, localStorage is ALSO written as a fallback even for
   * logged-in customers (per Doc 4 §4.4 fire-and-forget rule) so a Worker
   * failure doesn't lose the new logo. The cloud write is retried on
   * next popup open via a separate mechanism (not yet wired; Phase 2).
   *
   * @param {Object} logo - SavedLogo object per Doc 4 §2.2 (without
   *   last_used_at — server sets it on add/update; client sets it on
   *   localStorage write)
   * @returns {Promise<{ ok: boolean, logos: Array, source: string }>}
   *   Resolves with the updated full array (from Worker response if
   *   reachable, else the locally-merged array). `source` is 'cloud' if
   *   Worker write succeeded, 'local' if only localStorage was written.
   */
  /**
   * Decision B (Doc 4 §9, accepted 2026-05-15) — fail-open reassurance banner
   * on a cloud write failure. By the time a save/delete write rejects, the logo
   * is already in localStorage, so on a transient outage (5xx / 429 / network /
   * timeout) we surface the one-line reassurance line via the shell and let the
   * user proceed — the fallback is fail-open, not silent. A 4xx other than 429
   * means the popup sent a malformed request (a client bug), so it logs to
   * console only and shows no banner. Never throws: diagnostic UI must never
   * break the purchase-critical save path.
   *
   * @param {Error} [err] - the rejected _workerFetch error (may carry .status)
   */
  function _surfaceReassuranceOnFailure(err) {
    var status = (err && typeof err.status === 'number') ? err.status : 0;
    // 4xx other than 429 → client bug: console only, no banner (Doc 4 §9).
    if (status >= 400 && status < 500 && status !== 429) {
      if (window.console && console.warn) {
        console.warn('JRS.LogoService: cloud write rejected with ' + status +
          ' (client error) — saved to localStorage, no banner.');
      }
      return;
    }
    // 5xx / 429 / network / timeout → transient: fail-open reassurance banner.
    try {
      if (window.JRS && window.JRS.CustomizerPopup &&
          typeof window.JRS.CustomizerPopup.setReassurance === 'function') {
        window.JRS.CustomizerPopup.setReassurance(
          "Saved to this device. We'll sync when you're back online."
        );
      }
    } catch (e) { /* diagnostic UI must never break the save path */ }
  }

  function saveLogo(logo) {
    if (!logo || typeof logo.id !== 'string' || logo.id.length === 0) {
      return Promise.reject(new Error('saveLogo: invalid logo (missing id)'));
    }

    // Always write to localStorage first as the safety net. Logged-in users
    // get this as a fallback for Worker failures; guests use it as the
    // primary store.
    var localResult = _upsertLocalLogo(logo);

    if (!isLoggedIn()) {
      // Guest path — localStorage is the canonical store; we're done.
      return Promise.resolve({
        ok: localResult.ok,
        logos: localResult.logos,
        source: 'local'
      });
    }

    // Logged-in path — try the Worker. On success, the Worker's response
    // array is canonical (it has the server-set timestamps). On failure,
    // we keep the localStorage copy and report 'local'.
    return _workerFetch('POST', '/customer/logos', { logo: logo })
      .then(function(response) {
        if (response && response.ok && Array.isArray(response.logos)) {
          return { ok: true, logos: response.logos, source: 'cloud' };
        }
        // Worker returned but the response shape was unexpected.
        return { ok: localResult.ok, logos: localResult.logos, source: 'local' };
      })
      .catch(function(err) {
        // Network error, timeout, 4xx/5xx — fall back to localStorage. Decision
        // B (Doc 4 §9): surface the reassurance banner on a transient outage
        // (5xx/429/network); a 4xx client bug logs to console only. Never blocks.
        _surfaceReassuranceOnFailure(err);
        return { ok: localResult.ok, logos: localResult.logos, source: 'local' };
      });
  }

  /**
   * Touch an existing logo to refresh its last_used_at.
   *
   * Per Doc 4 §4.2 the touch flow:
   *   - Is debounced: at most one touch per (logo id, popup session)
   *   - Is fire-and-forget: failures silently no-op (touch is a sort-order
   *     improvement, not a data correctness concern)
   *   - For guests: updates localStorage only
   *   - For logged-in: POST to Worker /customer/logos/touch with body
   *     { logo: { id } } (per Doc 4 §5.4, v0.3 dedicated touch endpoint)
   *
   * @param {string} id - Logo id
   * @returns {Promise<boolean>} true on success, false on debounce / failure
   */
  function touchLogo(id) {
    if (typeof id !== 'string' || id.length === 0) {
      return Promise.resolve(false);
    }

    // Session debounce per Doc 4 §4.2
    if (_touchedThisSession[id]) {
      return Promise.resolve(false);
    }
    _touchedThisSession[id] = true;

    // Always update localStorage (cheap, no network) so guest sessions and
    // logged-in fallback both have a fresh sort order locally.
    _touchLocalLogo(id);

    if (!isLoggedIn()) {
      return Promise.resolve(true);
    }

    // Logged-in: POST to the dedicated touch endpoint (Doc 4 §5.4, v0.3).
    // Body is { logo: { id } } only — the touch endpoint validates the
    // partial body and updates last_used_at server-side.
    return _workerFetch('POST', '/customer/logos/touch', { logo: { id: id } })
      .then(function(response) {
        return !!(response && response.ok);
      })
      .catch(function() {
        // Silent no-op per Doc 4 §4.2. Sort-order staleness is acceptable.
        return false;
      });
  }

  /**
   * Reset the session-scoped touch debounce. The popup shell calls this
   * on every new OPEN dispatch so each popup session gets a fresh slate
   * for touch firing.
   */
  function resetTouchDebounce() {
    _touchedThisSession = {};
  }

  /**
   * Delete a logo from the customer's library.
   *
   * For logged-in: DELETE /customer/logos/:id. On success the Worker
   * returns the updated array sans the deleted entry.
   *
   * For guests: remove from localStorage.
   *
   * @param {string} id
   * @returns {Promise<{ ok: boolean, logos: Array, source: string }>}
   */
  function deleteLogo(id) {
    if (typeof id !== 'string' || id.length === 0) {
      return Promise.reject(new Error('deleteLogo: invalid id'));
    }

    // Always update localStorage to reflect the deletion locally
    var localResult = _removeLocalLogo(id);

    if (!isLoggedIn()) {
      return Promise.resolve({
        ok: localResult.ok,
        logos: localResult.logos,
        source: 'local'
      });
    }

    return _workerFetch('DELETE', '/customer/logos/' + encodeURIComponent(id))
      .then(function(response) {
        if (response && response.ok && Array.isArray(response.logos)) {
          return { ok: true, logos: response.logos, source: 'cloud' };
        }
        return { ok: localResult.ok, logos: localResult.logos, source: 'local' };
      })
      .catch(function(err) {
        // Decision B (Doc 4 §9) — same fail-open reassurance as saveLogo.
        _surfaceReassuranceOnFailure(err);
        return { ok: localResult.ok, logos: localResult.logos, source: 'local' };
      });
  }

  /**
   * Run the one-time guest-to-cloud migration per Doc 4 §8.
   *
   * Detection (Doc 4 §8.1): all three must be true:
   *   1. Customer is logged in
   *   2. localStorage.jrs.logos.v1 exists with ≥1 logo
   *   3. localStorage.jrs.migration.v1.done is NOT set
   *
   * Behaviour: POST the localStorage logos (with last_used_at back-filled)
   * to /customer/logos/migrate. On success, clear localStorage and set the
   * sentinel so this never runs again on this device.
   *
   * Idempotent (Doc 4 §8.3): if migration partially succeeds (Worker returns
   * 200 but client crashes before clearing localStorage), re-running merges
   * the same logos again with no duplication.
   *
   * @returns {Promise<{ migrated: boolean, count: number, reason?: string }>}
   *   - migrated: true if the migration ran AND succeeded; false otherwise
   *   - count: number of logos sent (0 if migration didn't run)
   *   - reason: string describing why migration didn't run, if applicable
   */
  function runMigration() {
    if (!isLoggedIn()) {
      return Promise.resolve({ migrated: false, count: 0, reason: 'not_logged_in' });
    }

    if (_isMigrationDone()) {
      return Promise.resolve({ migrated: false, count: 0, reason: 'already_done' });
    }

    var localLogos = _readLocalLogos();
    if (localLogos.length === 0) {
      // Nothing to migrate; set sentinel so we don't keep checking.
      _setMigrationDone();
      return Promise.resolve({ migrated: false, count: 0, reason: 'nothing_to_migrate' });
    }

    // Back-fill last_used_at per Doc 4 §8.2 before sending; the Worker's
    // migrate endpoint preserves incoming last_used_at if present (§5.5).
    var normalised = [];
    for (var i = 0; i < localLogos.length; i++) {
      normalised.push(_backFillLastUsedAt(localLogos[i]));
    }

    return _workerFetch('POST', '/customer/logos/migrate', { logos: normalised })
      .then(function(response) {
        if (response && response.ok) {
          // Successful merge — clear local copy and set sentinel
          try {
            localStorage.removeItem(LOCAL_STORAGE_KEY);
          } catch (e) {
            // Fail-silent; sentinel is set so we won't try again on this
            // device, but the next popup open will still read the now-stale
            // localStorage. Worst case the merge re-runs idempotently.
          }
          _setMigrationDone();
          return { migrated: true, count: normalised.length };
        }
        return { migrated: false, count: 0, reason: 'worker_error' };
      })
      .catch(function() {
        // Network error — leave localStorage in place, retry on next open
        return { migrated: false, count: 0, reason: 'network_error' };
      });
  }

  // ===========================================================================
  // v0.2 — Internal helpers
  // ===========================================================================

  /**
   * Parse the Liquid-rendered cloud-logos blob (Doc 4 §3.1).
   *
   * The blob is either:
   *   - Absent (guest customer) — returns []
   *   - null literal (logged-in customer with no saved logos) — returns []
   *   - { version, logos, updated_at } object — returns the `logos` array
   *
   * Malformed JSON or unexpected shapes return [] with a console.error.
   *
   * @returns {Array}
   */
  function _readCloudLogosBlob() {
    var tag = document.querySelector(CLOUD_BLOB_SELECTOR);
    if (!tag) return [];

    var raw = tag.textContent || '';
    if (raw === '' || raw === 'null') return [];

    try {
      var parsed = JSON.parse(raw);
      if (!parsed || !Array.isArray(parsed.logos)) return [];
      return parsed.logos;
    } catch (e) {
      console.error('JRS.LogoService: failed to parse customer logos metafield', e);
      return [];
    }
  }

  /**
   * Read the localStorage logo library (Doc 4 §6.2). Returns the `logos`
   * array (NOT the full envelope). Empty array on absence / malformed JSON /
   * unavailable localStorage (private mode etc).
   *
   * @returns {Array}
   */
  function _readLocalLogos() {
    try {
      var raw = localStorage.getItem(LOCAL_STORAGE_KEY);
      if (!raw) return [];
      var parsed = JSON.parse(raw);
      if (!parsed || !Array.isArray(parsed.logos)) return [];
      return parsed.logos;
    } catch (e) {
      console.error('JRS.LogoService: failed to read local logos', e);
      return [];
    }
  }

  /**
   * Write the localStorage logo library with last_used_at back-fill for
   * legacy entries (Doc 4 §6.3). Enforces the MAX_LOGOS cap.
   *
   * Returns success boolean. Failure (quota exceeded, private mode) is
   * logged but doesn't throw — the popup keeps working in-memory.
   *
   * @param {Array} logos
   * @returns {boolean}
   */
  function _writeLocalLogos(logos) {
    if (!Array.isArray(logos)) return false;
    try {
      var now = Date.now();
      var capped = logos.slice(0, MAX_LOGOS);
      var normalised = [];
      for (var i = 0; i < capped.length; i++) {
        normalised.push(_backFillLastUsedAt(capped[i]));
      }
      var payload = {
        version: 1,
        logos: normalised,
        updated_at: now
      };
      localStorage.setItem(LOCAL_STORAGE_KEY, JSON.stringify(payload));
      return true;
    } catch (e) {
      console.error('JRS.LogoService: failed to write local logos', e);
      return false;
    }
  }

  /**
   * Insert or replace a single logo in localStorage by id. Sets
   * uploaded_at = Date.now() for new entries; preserves it for updates.
   * Always sets last_used_at = Date.now() (the entry is "in use" right now).
   *
   * @param {Object} logo
   * @returns {{ ok: boolean, logos: Array }}
   */
  function _upsertLocalLogo(logo) {
    var existing = _readLocalLogos();
    var now = Date.now();
    var replaced = false;
    var nextLogos = [];

    for (var i = 0; i < existing.length; i++) {
      var entry = existing[i];
      if (entry && entry.id === logo.id) {
        // Update — preserve original uploaded_at, refresh last_used_at
        nextLogos.push(_merge(entry, _merge(logo, {
          uploaded_at: typeof entry.uploaded_at === 'number' ? entry.uploaded_at : now,
          last_used_at: now
        })));
        replaced = true;
      } else {
        nextLogos.push(entry);
      }
    }

    if (!replaced) {
      // Insert at the front (most-recent first)
      var fresh = _merge(logo, {
        uploaded_at: typeof logo.uploaded_at === 'number' ? logo.uploaded_at : now,
        last_used_at: now
      });
      nextLogos.unshift(fresh);
    }

    var ok = _writeLocalLogos(nextLogos);
    return { ok: ok, logos: ok ? nextLogos : existing };
  }

  /**
   * Update the last_used_at timestamp on one localStorage entry (Doc 4 §6.4).
   * No-op if the id isn't present.
   *
   * @param {string} id
   * @returns {boolean} true if updated
   */
  function _touchLocalLogo(id) {
    try {
      var raw = localStorage.getItem(LOCAL_STORAGE_KEY);
      if (!raw) return false;
      var parsed = JSON.parse(raw);
      if (!parsed || !Array.isArray(parsed.logos)) return false;

      var now = Date.now();
      var found = false;
      var updated = [];
      for (var i = 0; i < parsed.logos.length; i++) {
        var logo = parsed.logos[i];
        if (logo && logo.id === id) {
          updated.push(_merge(logo, { last_used_at: now }));
          found = true;
        } else {
          updated.push(logo);
        }
      }
      if (!found) return false;

      parsed.logos = updated;
      parsed.updated_at = now;
      localStorage.setItem(LOCAL_STORAGE_KEY, JSON.stringify(parsed));
      return true;
    } catch (e) {
      console.error('JRS.LogoService: failed to touch local logo', e);
      return false;
    }
  }

  /**
   * Remove a localStorage entry by id.
   * @param {string} id
   * @returns {{ ok: boolean, logos: Array }}
   */
  function _removeLocalLogo(id) {
    var existing = _readLocalLogos();
    var filtered = [];
    for (var i = 0; i < existing.length; i++) {
      if (existing[i] && existing[i].id !== id) filtered.push(existing[i]);
    }
    var ok = _writeLocalLogos(filtered);
    return { ok: ok, logos: ok ? filtered : existing };
  }

  /**
   * Back-fill last_used_at on a single logo using uploaded_at as fallback.
   * Returns a new object; does not mutate the input. (Doc 4 §3.2 / §6.3.)
   *
   * @param {Object} logo
   * @returns {Object}
   */
  function _backFillLastUsedAt(logo) {
    if (!logo) return logo;
    if (typeof logo.last_used_at === 'number') return logo;
    var fallback = typeof logo.uploaded_at === 'number' ? logo.uploaded_at : 0;
    return _merge(logo, { last_used_at: fallback });
  }

  /**
   * Worker request wrapper. Builds the App Proxy URL, sets headers, applies
   * the timeout, parses JSON, and returns the parsed response object.
   *
   * Errors (network, 4xx/5xx, timeout, malformed JSON) reject the Promise
   * so callers can decide whether to fallback to localStorage or silently
   * no-op (depending on which method called).
   *
   * @param {string} method - 'GET' | 'POST' | 'DELETE'
   * @param {string} path - Path under WORKER_BASE_PATH, with leading slash
   *                        e.g. '/customer/logos' or '/customer/logos/abc'
   * @param {Object} [body] - JSON-serialisable body for POST
   * @returns {Promise<Object>}
   */
  function _workerFetch(method, path, body) {
    var url = WORKER_BASE_PATH + path;
    var options = {
      method: method,
      headers: { 'Accept': 'application/json' },
      credentials: 'same-origin' // App Proxy needs the session cookie
    };

    if (body !== undefined && body !== null) {
      options.headers['Content-Type'] = 'application/json';
      options.body = JSON.stringify(body);
    }

    // AbortController-based timeout if supported, else fall back to a race
    // (older browsers — the Worker timeout below is the safety net).
    var controller = null;
    var timeoutId = null;
    if (typeof AbortController === 'function') {
      controller = new AbortController();
      options.signal = controller.signal;
      timeoutId = setTimeout(function() {
        try { controller.abort(); } catch (e) {}
      }, WORKER_TIMEOUT_MS);
    }

    return fetch(url, options).then(function(response) {
      if (timeoutId) clearTimeout(timeoutId);
      if (!response.ok) {
        // 4xx / 5xx — reject so caller can decide fallback. We still try
        // to parse the body so a structured error surfaces to console.
        return response.text().then(function(text) {
          var err = new Error('Worker returned status ' + response.status);
          err.status = response.status;
          err.body = text;
          throw err;
        });
      }
      return response.json();
    }).catch(function(err) {
      if (timeoutId) clearTimeout(timeoutId);
      throw err;
    });
  }

  /** @returns {boolean} */
  function _isMigrationDone() {
    try {
      return localStorage.getItem(MIGRATION_SENTINEL_KEY) === '1';
    } catch (e) {
      // If localStorage is unavailable we can't read or write the sentinel.
      // Treat as "done" to avoid repeatedly attempting the migration on
      // every popup open in a private-mode session.
      return true;
    }
  }

  function _setMigrationDone() {
    try {
      localStorage.setItem(MIGRATION_SENTINEL_KEY, '1');
    } catch (e) {
      // Fail-silent; without the sentinel the migration will retry next
      // open. Idempotent on the Worker side per Doc 4 §8.3.
    }
  }

  // ===========================================================================
  // v0.1 helpers (unchanged)
  // ===========================================================================

  /**
   * @param {string} filename
   * @returns {{ name: string, extension: string }}
   */
  function _splitFilename(filename) {
    var safe = typeof filename === 'string' ? filename : '';
    var dotIndex = safe.lastIndexOf('.');

    if (dotIndex <= 0 || dotIndex === safe.length - 1) {
      return {
        name: safe,
        extension: ''
      };
    }

    return {
      name: safe.slice(0, dotIndex),
      extension: safe.slice(dotIndex + 1).toLowerCase()
    };
  }

  /**
   * @param {string} fileName
   * @returns {string}
   */
  function _getExtension(fileName) {
    return _splitFilename(fileName).extension;
  }

  /**
   * @param {string} mimeType
   * @returns {boolean}
   */
  function _isRejectedMime(mimeType) {
    if (!mimeType) return false;
    if (REJECTED_MIME_TYPES[mimeType]) return true;
    return mimeType.indexOf('application/vnd') === 0;
  }

  /**
   * @returns {string}
   */
  // ERP parity (2026-08-04): control's numeric logo id — six digits or fewer,
  // minted per upload: String(Math.floor(Math.random() * (999998 + 1))).
  // The ERP and fulfilment key on this numeric form; UUIDs broke ingestion.
  // Existing saved-library UUIDs remain valid ids (dual reality accepted).
  function mintLogoId() {
    return String(Math.floor(Math.random() * (999998 + 1)));
  }

  function _safeGenerateUUID() {
    if (typeof generateUUID === 'function') {
      return generateUUID();
    }
    if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
      return crypto.randomUUID();
    }
    return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function(c) {
      var r = Math.random() * 16 | 0;
      var v = c === 'x' ? r : (r & 0x3 | 0x8);
      return v.toString(16);
    });
  }

  /**
   * @param {string} responseText
   * @returns {string}
   */
  function _extractFileUrl(responseText) {
    var raw = String(responseText || '').replace(/^\s+|\s+$/g, '');
    if (!raw) return '';

    try {
      var parsed = JSON.parse(raw);
      if (typeof parsed === 'string') {
        return parsed.replace(/^\s+|\s+$/g, '');
      }
      if (parsed && typeof parsed.url === 'string') return parsed.url.replace(/^\s+|\s+$/g, '');
      if (parsed && typeof parsed.fileUrl === 'string') return parsed.fileUrl.replace(/^\s+|\s+$/g, '');
      if (parsed && typeof parsed.file_url === 'string') return parsed.file_url.replace(/^\s+|\s+$/g, '');
      if (parsed && typeof parsed.location === 'string') return parsed.location.replace(/^\s+|\s+$/g, '');
      if (parsed && typeof parsed.path === 'string') return parsed.path.replace(/^\s+|\s+$/g, '');

      // 2026-05-18 — filePath branch per Doc 5 §4.3.
      // Legacy PHP upload server at jrsindustrial-files.com returns
      // {filePath: "uploads/<filename>"} with no leading slash.
      // Client must concatenate UPLOAD_ENDPOINT + "/" + filePath to produce
      // the full HTTPS URL. This branch was missing in the original
      // _extractFileUrl implementation, causing all uploads through the
      // new popup to fail validation at uploadFile()'s HTTPS gate
      // ("did not return a valid HTTPS URL"). Discovered in Phase 2 QA
      // 2026-05-18.
      if (parsed && typeof parsed.filePath === 'string') {
        var filePath = parsed.filePath.replace(/^\s+|\s+$/g, '');
        if (!filePath) return '';
        if (filePath.charAt(0) === '/') filePath = filePath.slice(1);
        return UPLOAD_ENDPOINT.replace(/\/+$/, '') + '/' + filePath;
      }
    } catch (e) {
      void e;
    }

    return raw;
  }

  /**
   * Shallow merge of two plain objects; returns a new object.
   * @param {Object} a
   * @param {Object} b
   * @returns {Object}
   */
  function _merge(a, b) {
    var out = {};
    var k;
    if (a) {
      for (k in a) {
        if (Object.prototype.hasOwnProperty.call(a, k)) out[k] = a[k];
      }
    }
    if (b) {
      for (k in b) {
        if (Object.prototype.hasOwnProperty.call(b, k)) out[k] = b[k];
      }
    }
    return out;
  }

  // ===========================================================================
  // Export
  // ===========================================================================

  JRS.LogoService = {
    VERSION: VERSION,

    // v0.1 — file upload
    uploadFile: uploadFile,
    validateFile: validateFile,
    sanitizeFilename: sanitizeFilename,
    buildUploadFilename: buildUploadFilename,

    // 2026-08-01 — non-image (PDF) preview helpers, shared by every logo
    // thumbnail surface (dropzone preview, saved-logo tiles, bundle summary).
    mintLogoId: mintLogoId,
    isDisplayableImage: isDisplayableImage,
    nonImageThumb: nonImageThumb,
    previewSrcFor: previewSrcFor,

    // v0.2 — persistence layer
    readSavedLogos: readSavedLogos,
    saveLogo: saveLogo,
    touchLogo: touchLogo,
    deleteLogo: deleteLogo,
    runMigration: runMigration,
    isLoggedIn: isLoggedIn,
    resetTouchDebounce: resetTouchDebounce,

    // Exposed for testing (Phase 2 QA fix 2026-05-18). The underscore
    // prefix marks this as internal-but-exposed — it is NOT part of the
    // day-to-day public API. The regression test at
    // __tests__/logo-service-extract-file-url.test.js exercises this
    // entry point.
    _extractFileUrl: _extractFileUrl,

    // Exposed for testing (Decision B wiring, 2026-07-06). Exercised by
    // __tests__/logo-service-reassurance.test.js.
    _surfaceReassuranceOnFailure: _surfaceReassuranceOnFailure
  };
})(window.JRS);
